import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  PinInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { completeProfile, requestCode, verifyCode } from "./session";
import styles from "./SignIn.module.scss";

/**
 * Sign-in for the host panel (spec §4.2, §4.3, §4.5).
 *
 * Three steps, in the order spec §4.2 sets out: an address, then the six digits, then — for a
 * first-timer — a display name and the terms.
 *
 * The customer application has its own — `apps/customer/src/auth/SignInOverlay.tsx` —
 * and the two are deliberately not shared. This is a full-page card somebody sits down in front of;
 * that one is spec §4.4's overlay in the middle of a live show, and the note there sets out what
 * differs and why.
 *
 * The steps share one card (`AuthShell`) and swap only its contents, so the whole thing reads as one
 * screen. Each step carries its own heading and one line of explanation, because "check your email"
 * is the moment an operator is most likely to be confused about what just happened.
 *
 * ## Why the code is a `PinInput`
 *
 * Six separate boxes rather than one text field, with `oneTimeCode` autocomplete. The autocomplete
 * hint is what lets a phone offer the code from the notification banner without the mail app being
 * opened, which is `R-AUTH-7`'s latency argument playing out in one attribute: the round trip is an
 * email, and the fastest version of it never leaves the page.
 *
 * ## Errors are the API's words
 *
 * Every failure shown here is the `error` string the API sent, not a message reconstructed from a
 * status code. The API is the only place that knows whether a code was wrong, expired or exhausted,
 * and `R-SEC-4` means some of those deliberately read the same — a client that composed its own
 * message from the status would eventually make a distinction the requirement forbids.
 */
type Step = { name: "email" } | { name: "code"; email: string } | { name: "profile" };

export function SignIn({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>({ name: "email" });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  /**
   * One place where a request is made, its failure is turned into something showable, and `busy` is
   * cleared. Repeating this per step is how one of them ends up able to be submitted twice.
   */
  async function attempt(work: () => Promise<void>) {
    setBusy(true);
    setProblem(undefined);
    try {
      await work();
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.detail
          ? error.detail
          : t("signIn.unavailable"),
      );
    } finally {
      setBusy(false);
    }
  }

  const onRequestCode = () =>
    attempt(async () => {
      const requested = await requestCode(email);
      // The API answers with the address it normalised — lower-cased, trimmed. Carrying that forward
      // rather than the raw input means the verify call sends exactly what the code was issued
      // against, and that the address shown back is the one that was actually used.
      setStep({ name: "code", email: requested.email });
      setCode("");
    });

  const onVerify = (value: string) =>
    attempt(async () => {
      const verified = await verifyCode(email, value);
      if (verified.needsProfile) {
        setStep({ name: "profile" });
        return;
      }
      await onSignedIn();
    });

  const onCompleteProfile = () =>
    attempt(async () => {
      await completeProfile(name, consent);
      await onSignedIn();
    });

  const heading = (title: string, lede: string) => (
    <header className={styles.header}>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.lede}>{lede}</p>
    </header>
  );

  return (
    <>
      {step.name === "email" ? (
        <>
          {heading(t("signIn.heading"), t("signIn.emailHelp"))}
          <Stack gap="md">
            {problem ? (
              <Alert color="red" variant="light" radius="md">
                {problem}
              </Alert>
            ) : null}
            <TextInput
              label={t("signIn.emailLabel")}
              placeholder={t("signIn.emailPlaceholder")}
              type="email"
              size="md"
              // `username` and not `email`: it is what password managers and both mobile keyboards
              // recognise as "the identifier for this account", which is exactly what it is here.
              autoComplete="username"
              // The only field on the screen, and the operator came here to type in it. Autofocus is
              // usually worth arguing about; on a single-purpose sign-in step it is not.
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && email) void onRequestCode();
              }}
              disabled={busy}
            />
            <Button
              size="md"
              fullWidth
              onClick={() => void onRequestCode()}
              loading={busy}
              disabled={!email}
            >
              {t("signIn.sendCode")}
            </Button>
          </Stack>
        </>
      ) : null}

      {step.name === "code" ? (
        <>
          {heading(t("signIn.codeHeading"), t("signIn.codeHelp"))}
          <Stack gap="md">
            {problem ? (
              <Alert color="red" variant="light" radius="md">
                {problem}
              </Alert>
            ) : null}
            <Text size="sm" ta="center" c="dimmed">
              <span className={styles.recipient}>{step.email}</span>
            </Text>
            <PinInput
              length={6}
              type="number"
              inputMode="numeric"
              size="md"
              // The attribute that lets a phone offer the code straight from its notification,
              // without the mail app being opened at all.
              oneTimeCode
              autoFocus
              className={styles.code}
              value={code}
              onChange={setCode}
              // Submitted on the sixth digit rather than behind a button. There is nothing else to
              // decide once six digits are in, and a live show is not the place to add a tap.
              onComplete={(value) => void onVerify(value)}
              disabled={busy}
            />
            {/* `R-SEC-7` belongs in the interface as well as in the email. Somebody being talked
                through a sign-in over the phone is looking at this screen, not at their inbox. */}
            <p className={styles.warning}>{t("signIn.codeWarning")}</p>
            <div className={styles.actions}>
              <Button
                variant="subtle"
                color="ink"
                size="compact-sm"
                onClick={() => setStep({ name: "email" })}
                disabled={busy}
              >
                {t("signIn.changeEmail")}
              </Button>
              <Button
                variant="subtle"
                color="ink"
                size="compact-sm"
                onClick={() => void onRequestCode()}
                disabled={busy}
              >
                {t("signIn.resend")}
              </Button>
            </div>
          </Stack>
        </>
      ) : null}

      {step.name === "profile" ? (
        <>
          {heading(t("signIn.profileHeading"), t("signIn.profileHelp"))}
          <Stack gap="md">
            {problem ? (
              <Alert color="red" variant="light" radius="md">
                {problem}
              </Alert>
            ) : null}
            <TextInput
              label={t("signIn.displayNameLabel")}
              description={t("signIn.displayNameHelp")}
              size="md"
              autoFocus
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && name.trim().length >= 2) {
                  void onCompleteProfile();
                }
              }}
              disabled={busy}
            />
            <Checkbox
              label={t("signIn.marketingLabel")}
              checked={consent}
              onChange={(event) => setConsent(event.currentTarget.checked)}
              disabled={busy}
            />
            <Button
              size="md"
              fullWidth
              onClick={() => void onCompleteProfile()}
              loading={busy}
              disabled={name.trim().length < 2}
            >
              {t("signIn.finish")}
            </Button>
            <p className={styles.warning}>{t("signIn.termsNotice")}</p>
          </Stack>
        </>
      ) : null}
    </>
  );
}
